import Application from "../Application";
import logging from '../logging';
import { Object3D, LineBasicMaterial, MeshBasicMaterial, Mesh, BufferGeometry, EdgesGeometry, Material, LineSegments } from "three";
import WallView, {EXTENSION_LINE_MATERIAL} from "../views/WallView";
import createTechDrawingLabel from "./createTechDrawingLabel";

const log = logging('utils');

/**
 * In an earlier version, the AssemblyBuilder handled creating the 3D objects
 * with the technical document materials. This does the same thing so that
 * the old code may run unmodified with the new domain model and view classes.
 */
const toTechnicalDocument = (root: Object3D, isMetric: boolean, doorOpacity: number = 1) => {

    const
        lineMaterial = new LineBasicMaterial(
            { color: 0x000000, linewidth: 2 }),
        background = new MeshBasicMaterial(
            { color: 0xFFFFFF, opacity: 1, transparent: false }),
        transparent = new MeshBasicMaterial(
            { color: 0x000000, opacity: 0.25, transparent: true }),
        doorMaterial = new MeshBasicMaterial(
            { color: 0xFFFFFF, opacity: doorOpacity, transparent: doorOpacity != 1 }),
        meshes: Mesh[] = [],
        cache = new Map<BufferGeometry, EdgesGeometry>();

    root.traverse(obj => {
        if ((obj as any).isMesh && obj.visible && !obj.userData.isConvertedForTechDoc) {
            const mesh = obj as Mesh;
            meshes.push(mesh);
        }
    });
    for (const mesh of meshes) {
        try {
            // skip any labels that have been added prior to this call
            if(mesh.userData.isTechLabelMesh) continue;
            if(mesh.material == EXTENSION_LINE_MATERIAL) {
                mesh.removeFromParent();
                continue;
            }

            let material: Material;
            if (mesh.userData.isDoorMesh) {
                mesh.material = doorMaterial;
            } else {
                if (Array.isArray(mesh.material)) {
                    material = mesh.material[0];
                } else {
                    material = mesh.material;
                }
                switch (material.name) {
                    case 'Glass':
                    case 'Mirror':
                    case 'FrostedGlass':
                        mesh.material = transparent;
                        break;
                    default:
                        mesh.material = background;
                }
            }

            let edges: EdgesGeometry;
            if (mesh.geometry == null) {
                continue;
            }
            else if (cache.has(mesh.geometry)) {
                edges = cache.get(mesh.geometry);
            } else {
                const pos = mesh.geometry.getAttribute('position');
                if (pos && pos.count > 0) {
                    edges = new EdgesGeometry(mesh.geometry);
                    cache.set(mesh.geometry, edges);
                }
            }

            const lines = new LineSegments(edges, lineMaterial);

            lines.position.copy(mesh.position);
            lines.rotation.copy(mesh.rotation);
            lines.scale.copy(mesh.scale);
            mesh.parent.add(lines);
            mesh.userData.isConvertedForTechDoc = true;
        } catch (error) {
            log.error(error);
        }
    }

    createWallHeightLabels(root, isMetric);
    
    Application.do('pipeline-update');
};

const createWallHeightLabels = (root: Object3D, isMetric: boolean) => {
    root.traverse(obj => {
        if(obj instanceof WallView && !obj.model.island) {
            const wall = obj.model;

            for(const point of wall.heights) {
                const
                    height = Math.round(point.height * 1000) / 1000,
                    offset = wall.leftExtension ?? 0;
                createTechDrawingLabel(
                    String(isMetric ? Math.round(25.4*height) : height),
                    2.8, // inches
                    point.x + offset, // inches
                    point.height, // inches
                    root,
                );
            }
        }
    });
};

export default toTechnicalDocument;
